/*
 * Hairdealz (Claude, Aug 2026) — accessibility layer for the product information tabs.
 *
 * MinimogTheme.Tabs already handles click-to-switch by toggling an `active` class on
 * `.m-tab-header` / `.m-tab-content`. It does not manage focus or ARIA state. Rather than
 * fork that core class, this script sits on top of it:
 *
 *   - mirrors `aria-selected` and roving `tabindex` off the `active` class it sets
 *   - adds Left/Right/Home/End keyboard navigation across the tablist
 *   - keeps the focused tab visible in the horizontally scrolling header on mobile
 *
 * Activation is delegated back to the theme by dispatching a click, so there is exactly one
 * source of truth for which tab is open.
 */
(function () {
  'use strict';

  var SECTION = 'm-product-details-tabs';
  var HEADER = '.m-tab-header';

  function init(root) {
    var tablist = root.querySelector('[role="tablist"]');
    if (!tablist || tablist.dataset.a11yReady === 'true') return;

    var tabs = Array.prototype.slice.call(tablist.querySelectorAll(HEADER));
    if (!tabs.length) return;

    tablist.dataset.a11yReady = 'true';

    function sync() {
      tabs.forEach(function (tab) {
        var active = tab.classList.contains('active');
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
    }

    // The theme toggles `active` outside of our control, so watch for it rather than
    // trying to predict it.
    var observer = new MutationObserver(sync);
    tabs.forEach(function (tab) {
      observer.observe(tab, { attributes: true, attributeFilter: ['class'] });
    });
    sync();

    function activate(index) {
      var target = tabs[index];
      if (!target) return;
      target.click();
      target.focus();
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    tablist.addEventListener('keydown', function (event) {
      var current = tabs.indexOf(document.activeElement);
      if (current === -1) return;

      var next;
      switch (event.key) {
        case 'ArrowRight':
          next = (current + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          next = (current - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      activate(next);
    });
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll(SECTION);
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: sections are re-rendered on edit, so wire up the new markup.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
